import React from 'react';
import { useTranslation } from 'react-i18next';
import { appDateTime } from '@/utils/appDateFormat';
import { CheckCircle2, XCircle } from 'lucide-react';
import Button from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { Tooltip } from '@/components/ui/Tooltip';
import { DataSource, isPullMethod } from '../types';

interface SourceCardProps {
  source: DataSource;
  removable: boolean;
  blockedByCount: number;
  testing: boolean;
  onTest: () => void;
  onRemove: () => void;
  onViewExchange: () => void;
}

const SourceCard: React.FC<SourceCardProps> = ({
  source, removable, blockedByCount, testing, onTest, onRemove, onViewExchange,
}) => {
  const { t } = useTranslation();

  // A scheduled feed stores a time of day rather than a timestamp, so fall back to the raw value.
  const formatWhen = (value: string) => {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : appDateTime(parsed);
  };

  return (
    <div
      className="flex flex-wrap items-center gap-3 border-t border-border px-3 py-3"
      data-testid={`source-card-${source.id}`}
    >
      <div className="min-w-0 flex-1">
        <span className="text-sm font-medium text-foreground">{source.displayName}</span>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {t(`extInt.system.${source.system}`)}
          {source.tenantUrl ? ` · ${source.tenantUrl}` : ''}
        </p>
        {source.connectedByName && (
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t('extInt.config.connectedBy', { name: source.connectedByName })}
          </p>
        )}
        {source.lastRunOn && (
          <p className="mt-0.5 text-xs text-muted-foreground" data-testid={`source-last-run-${source.id}`}>
            {t('extInt.config.lastRun', { when: formatWhen(source.lastRunOn) })}
          </p>
        )}
        {/* How current the data is. Every connection has an answer — a pull states its interval, a
            push states that it arrives as changes happen — so this line is never absent. */}
        {source.method && (
          <p className="mt-0.5 text-xs text-muted-foreground" data-testid={`source-freshness-${source.id}`}>
            {isPullMethod(source.method)
              ? t(`extInt.wizard.freshness.${source.syncFrequency ?? 'daily'}`)
              : t('extInt.wizard.freshness.push')}
          </p>
        )}
        {source.nextScheduledRun && (
          <p className="mt-0.5 text-xs text-muted-foreground" data-testid={`source-next-run-${source.id}`}>
            {t('extInt.config.nextRun', { when: formatWhen(source.nextScheduledRun) })}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2">
        {source.status === 'error' ? (
          <XCircle className="h-4 w-4 text-red-600" aria-hidden="true" />
        ) : (
          <CheckCircle2 className="h-4 w-4 text-green-600" aria-hidden="true" />
        )}
        <span className="text-xs text-muted-foreground">{t('extInt.config.scopes')}</span>
      </div>

      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" onClick={onTest} disabled={testing} data-testid={`source-test-${source.id}`}>
          {testing && <Spinner size="sm" className="me-2" />}
          {t('extInt.config.test')}
        </Button>
        <Button size="sm" variant="ghost" onClick={onViewExchange} data-testid={`source-exchange-${source.id}`}>
          {t('extInt.config.viewExchange')}
        </Button>
        {removable ? (
          <Button size="sm" variant="destructive" onClick={onRemove} data-testid={`source-remove-${source.id}`}>
            {t('extInt.config.remove')}
          </Button>
        ) : (
          <Tooltip content={t('extInt.config.removeBlocked', { count: blockedByCount })}>
            <span>
              <Button size="sm" variant="destructive" disabled data-testid={`source-remove-${source.id}`}>
                {t('extInt.config.remove')}
              </Button>
            </span>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default SourceCard;
